import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiMail, FiPhone, FiMapPin, FiArrowUpRight, FiDownload } from "react-icons/fi";
import { Section, Wrapper } from "../SectionHead";
import { fadeUp, viewportOnce } from "../../utils/motion";
import { Bio } from "../../data/constants";

const Panel = styled(motion.div)`
  position: relative;
  padding: 80px 40px;
  border-radius: 28px;
  background: radial-gradient(ellipse 60% 100% at 50% 0%, rgba(139, 124, 255, 0.14), transparent),
    rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
  overflow: hidden;

  @media (max-width: 640px) {
    padding: 56px 22px;
  }
`;

const Eyebrow = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 100px;
  background: rgba(139, 124, 255, 0.08);
  border: 1px solid rgba(139, 124, 255, 0.25);
  color: ${({ theme }) => theme.primary};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 22px;
`;

const Heading = styled(motion.h2)`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 600;
  font-size: clamp(2rem, 5vw, 3.2rem);
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => theme.text_primary};
  max-width: 720px;
  margin: 0 auto;
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const SubText = styled(motion.p)`
  max-width: 520px;
  margin: 20px auto 0;
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};
`;

const CTARow = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 38px;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 16px 28px;
  border-radius: 100px;
  background: ${({ theme }) => theme.gradient};
  color: #050507;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 34px rgba(139, 124, 255, 0.35);
  }
`;

const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 16px 28px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: ${({ theme }) => theme.text_primary};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background: rgba(139, 124, 255, 0.08);
    transform: translateY(-3px);
  }
`;

const InfoRow = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 48px;
  padding-top: 36px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const InfoChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13.5px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.text_primary};
    border-color: ${({ theme }) => theme.primary};
  }
`;

const Contact = () => {
  return (
    <Section id="contact" style={{ scrollMarginTop: 96 }}>
      <Wrapper>
        <Panel variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={0}>
          <Eyebrow variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={0}>
            Get In Touch
          </Eyebrow>
          <Heading variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={1}>
            Let's talk about what <GradientText>you're building</GradientText>.
          </Heading>
          <SubText variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={2}>
            I'm open to Lead or Senior Engineering roles, infrastructure consulting, and
            select freelance work. Send me an email and tell me what you're working on.
          </SubText>

          <CTARow variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={3}>
            <PrimaryButton href={`mailto:${Bio.email}`}>
              <FiMail /> Email Me
            </PrimaryButton>
            <GhostButton href={Bio.resume} target="_blank" rel="noopener noreferrer">
              <FiDownload /> Download Resume
            </GhostButton>
          </CTARow>

          <InfoRow variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} custom={4}>
            <InfoChip href={`mailto:${Bio.email}`}>
              <FiMail size={14} /> {Bio.email}
            </InfoChip>
            <InfoChip href={`tel:${Bio.phone.replace(/\s+/g, "")}`}>
              <FiPhone size={14} /> {Bio.phone}
            </InfoChip>
            <InfoChip as="span" style={{ cursor: "default" }}>
              <FiMapPin size={14} /> {Bio.location}
            </InfoChip>
            <InfoChip href={Bio.linkedin} target="_blank" rel="noopener noreferrer">
              LinkedIn <FiArrowUpRight size={13} />
            </InfoChip>
          </InfoRow>
        </Panel>
      </Wrapper>
    </Section>
  );
};

export default Contact;
